/**
 * Data Transformers
 * Convert between API format and React Flow format
 */

import { WorkflowNode, WorkflowConnection, SYSTEM_MY_COMPONENTS_PORT, SYSTEM_COMPONENT_PORT } from '@flow-studio/types';
import { Node, Edge } from 'reactflow';

/**
 * Convert workflow nodes to React Flow nodes
 * CRITICAL: Preserve processElementId - it's needed by the ConnectorConfigDialog for TIER 1 calls
 */
export const toReactFlowNodes = (nodes: WorkflowNode[]): Node[] => {
  return nodes.map((node) => {
    // Inject system ports (my-components + component) if not already present in the template
    let data = node.data;
    const pe = data?.processElement;
    if (pe) {
      const ports: any[] = pe.ports ?? [];
      const hasMyComponents = ports.some((p) => p.portKey === 'my-components');
      const hasComponent = ports.some((p) => p.portKey === 'component');
      if (!hasMyComponents || !hasComponent) {
        data = {
          ...data,
          processElement: {
            ...pe,
            ports: [
              ...ports,
              ...(!hasMyComponents ? [SYSTEM_MY_COMPONENTS_PORT] : []),
              ...(!hasComponent ? [SYSTEM_COMPONENT_PORT] : []),
            ],
          },
        };
      }
    }

    return {
      id: node.id,
      type: node.type,
      position: node.position,
      data,
      selected: node.selected,
      dragging: node.dragging,
      draggable: true,
      selectable: true,
      connectable: true,
      processElementId: node.processElementId,
    };
  });
};

/**
 * Convert React Flow nodes to workflow nodes
 * CRITICAL: Preserve processElementId - it gets added by toReactFlowNodes and is needed for TIER 1 calls
 */
export const fromReactFlowNodes = (nodes: Node[]): WorkflowNode[] => {
  return nodes.map((node) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { width, height, measured, ...rest } = node as any;
    return {
      ...rest,
      data: node.data || {},
      processElementId: (node as any).processElementId,
    };
  }) as WorkflowNode[];
};

/**
 * Convert workflow connections to React Flow edges
 */
export const toReactFlowEdges = (connections: WorkflowConnection[]): Edge[] => {
  return connections.map((conn) => ({
    id: conn.id,
    source: conn.source,
    target: conn.target,
    sourceHandle: conn.sourceHandle,
    targetHandle: conn.targetHandle,
    type: conn.type || 'smoothstep',
    label: conn.label,
    labelStyle: (conn as any).labelStyle,
    labelShowBg: (conn as any).labelShowBg,
    labelBgStyle: (conn as any).labelBgStyle,
    animated: conn.animated,
    style: conn.style as any,
    markerEnd: (conn as any).markerEnd,
    markerStart: (conn as any).markerStart,
    zIndex: (conn as any).zIndex,
    hidden: (conn as any).hidden,
    className: (conn as any).className,
    data: conn.data,
    selected: conn.selected,
  }));
};

/**
 * Convert React Flow edges to workflow connections
 *
 * CRITICAL: Preserve sourceHandle and targetHandle exactly as React Flow provides them.
 * Do not default to 'main' as this breaks specialized nodes like AI Agent which have
 * custom input ports (input, inheritfrom, participant).
 *
 * React Flow will provide the correct handle IDs when connections are created.
 * If they're missing, it's better to preserve undefined than to default to 'main'.
 */
export const fromReactFlowEdges = (edges: Edge[]): WorkflowConnection[] => {
  return edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    // Only default sourceHandle to 'main' if truly missing (for backward compatibility)
    // targetHandle should be preserved as-is to maintain specialized port mappings
    sourceHandle: edge.sourceHandle || 'main',
    targetHandle: edge.targetHandle || 'main',
    type: (edge.type as any) || 'smoothstep',
    label: edge.label as string,
    animated: edge.animated,
    style: edge.style as any,
    data: edge.data,
    selected: edge.selected,
  }));
};

/**
 * Generate unique ID
 */
export const generateId = (prefix: string = 'id'): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Clone node with new ID and offset position
 * NOTE: New nodes won't have processElementId (it's generated by the backend on save)
 */
export const cloneNode = (node: WorkflowNode, offset: number = 50): WorkflowNode => {
  const newId = generateId('node');
  return {
    ...node,
    id: newId,
    processElementId: undefined,
    data: {
      ...node.data,
      processElement: {
        ...(node.data.processElement as any),
        processElementID: undefined,
        processElementKey: newId,
        connectorID: undefined,
        connector: { configuration: (node.data.processElement as any)?.connector?.configuration ?? {} },
        name: generateId(node.type),
        displayName: `${(node.data.processElement as any)?.displayName} (Copy)`,
      } as any,
      runtime: {
        ...(node.data.runtime as any),
        executionStatus: 'idle' as const,
      } as any,
    },
    position: {
      x: node.position.x + offset,
      y: node.position.y + offset,
    },
    selected: false,
  };
};

/**
 * Find connected edges for a node
 */
export const getConnectedEdges = (
  nodeId: string,
  edges: WorkflowConnection[]
): WorkflowConnection[] => {
  return edges.filter(
    (edge) => edge.source === nodeId || edge.target === nodeId
  );
};

/**
 * Check if edge creates a cycle
 */
export const wouldCreateCycle = (
  source: string,
  target: string,
  edges: WorkflowConnection[]
): boolean => {
  const visited = new Set<string>();
  const stack = [target];

  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === source) return true;
    if (visited.has(current)) continue;

    visited.add(current);
    const outgoingEdges = edges.filter((e) => e.source === current);
    stack.push(...outgoingEdges.map((e) => e.target));
  }

  return false;
};
